/* global axios */
import ApiClient from '../ApiClient';

// Everything is scoped by inbox: a Meta Business Agent belongs to one WhatsApp
// phone number, so there is at most one agent per inbox.
class MetaAgentAPI extends ApiClient {
  constructor() {
    super('meta_agent/inboxes', { accountScoped: true });
  }

  getInboxes() {
    return axios.get(this.url);
  }

  getAgent(inboxId) {
    return axios.get(`${this.url}/${inboxId}/agent`);
  }

  checkEligibility(inboxId) {
    return axios.post(`${this.url}/${inboxId}/agent/eligibility`);
  }

  attestTerms(inboxId) {
    return axios.post(`${this.url}/${inboxId}/agent/attest_terms`);
  }

  onboard(inboxId) {
    return axios.post(`${this.url}/${inboxId}/agent`);
  }

  deleteAgent(inboxId) {
    return axios.delete(`${this.url}/${inboxId}/agent`);
  }

  getSettings(inboxId) {
    return axios.get(`${this.url}/${inboxId}/settings`);
  }

  // Meta replaces settings wholesale, so the caller always sends every field.
  updateSettings(inboxId, { settings, confirm = false }) {
    return axios.put(`${this.url}/${inboxId}/settings`, { settings, confirm });
  }

  // Hands the conversation back to the agent. Taking control has no endpoint:
  // replying is what takes it.
  releaseThread(conversationId) {
    return axios.post(
      `${this.baseUrl()}/meta_agent/conversations/${conversationId}/release`
    );
  }

  getBusinessInfo(inboxId) {
    return axios.get(`${this.url}/${inboxId}/business_info`);
  }

  // Meta replaces the record wholesale, so the form always sends every field.
  updateBusinessInfo(inboxId, businessInfo) {
    return axios.put(`${this.url}/${inboxId}/business_info`, {
      business_info: businessInfo,
    });
  }

  getFaqs(inboxId) {
    return axios.get(`${this.url}/${inboxId}/faqs`);
  }

  createFaq(inboxId, faq) {
    return axios.post(`${this.url}/${inboxId}/faqs`, { faq });
  }

  updateFaq(inboxId, faqId, faq) {
    return axios.put(`${this.url}/${inboxId}/faqs/${faqId}`, { faq });
  }

  deleteFaq(inboxId, faqId) {
    return axios.delete(`${this.url}/${inboxId}/faqs/${faqId}`);
  }

  getWebsites(inboxId) {
    return axios.get(`${this.url}/${inboxId}/websites`);
  }

  createWebsite(inboxId, url) {
    return axios.post(`${this.url}/${inboxId}/websites`, { url });
  }

  deleteWebsite(inboxId, websiteId) {
    return axios.delete(`${this.url}/${inboxId}/websites/${websiteId}`);
  }

  getFiles(inboxId) {
    return axios.get(`${this.url}/${inboxId}/files`);
  }

  uploadFile(inboxId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${this.url}/${inboxId}/files`, formData);
  }

  deleteFile(inboxId, fileId) {
    return axios.delete(`${this.url}/${inboxId}/files/${fileId}`);
  }

  getSkills(inboxId) {
    return axios.get(`${this.url}/${inboxId}/skills`);
  }

  createSkill(inboxId, skill) {
    return axios.post(`${this.url}/${inboxId}/skills`, { skill });
  }

  updateSkill(inboxId, skillId, skill) {
    return axios.put(`${this.url}/${inboxId}/skills/${skillId}`, { skill });
  }

  deleteSkill(inboxId, skillId) {
    return axios.delete(`${this.url}/${inboxId}/skills/${skillId}`);
  }

  getUiSkills(inboxId) {
    return axios.get(`${this.url}/${inboxId}/ui_skills`);
  }

  createUiSkill(inboxId, uiSkill) {
    return axios.post(`${this.url}/${inboxId}/ui_skills`, {
      ui_skill: uiSkill,
    });
  }

  updateUiSkill(inboxId, uiSkillId, uiSkill) {
    return axios.put(`${this.url}/${inboxId}/ui_skills/${uiSkillId}`, {
      ui_skill: uiSkill,
    });
  }

  deleteUiSkill(inboxId, uiSkillId) {
    return axios.delete(`${this.url}/${inboxId}/ui_skills/${uiSkillId}`);
  }

  // Runs the full agent pipeline without a consumer number. Not billed.
  // Passing back the conversation_id from a previous turn continues the thread.
  sendPlaygroundMessage(inboxId, { userMsg, conversationId }) {
    return axios.post(`${this.url}/${inboxId}/playground`, {
      user_msg: userMsg,
      conversation_id: conversationId,
    });
  }

  getAllowlist(inboxId) {
    return axios.get(`${this.url}/${inboxId}/allowlist_entries`);
  }

  addAllowlistEntry(inboxId, consumerPhoneNumber) {
    return axios.post(`${this.url}/${inboxId}/allowlist_entries`, {
      consumer_phone_number: consumerPhoneNumber,
    });
  }

  removeAllowlistEntry(inboxId, entryId) {
    return axios.delete(`${this.url}/${inboxId}/allowlist_entries/${entryId}`);
  }
}

export default new MetaAgentAPI();
